import React, { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { tasksAPI, usersAPI, formatError } from "../utils/api";
import { Plus, X, MessageCircle, CheckCircle, Clock, AlertCircle, Lock, Users } from "lucide-react";
import { useConfirm } from "../hooks/useConfirm";
import { fmtDateTime, fmtDate } from "../utils/dateUtils";

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto animate-fade-in-up">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E1DB] sticky top-0 bg-white">
          <h2 className="font-bold text-lg font-[Manrope] text-[#3A322C]">{title}</h2>
          <button onClick={onClose}><X size={20} /></button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}

// ── Multi-Assignee Selector ──────────────────────────────────────────────────
function AssigneeSelector({ users, selectedIds = [], onChange }) {
  const [open, setOpen] = useState(false);

  const toggle = (id) => {
    const next = selectedIds.includes(id)
      ? selectedIds.filter(x => x !== id)
      : [...selectedIds, id];
    onChange(next);
  };

  const selectedNames = users
    .filter(u => selectedIds.includes(u.id))
    .map(u => u.name);

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D] text-left flex items-center justify-between"
      >
        <span className={selectedNames.length ? "text-[#3A322C]" : "text-[#8A7E73]"}>
          {selectedNames.length
            ? selectedNames.join(", ")
            : "Select people…"}
        </span>
        <Users size={14} className="text-[#8A7E73] shrink-0" />
      </button>

      {open && (
        <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-white border border-[#E5E1DB] rounded-xl shadow-lg max-h-52 overflow-y-auto">
          {users.length === 0 && (
            <p className="px-4 py-3 text-sm text-[#8A7E73]">No users found</p>
          )}
          {users.map(u => (
            <label key={u.id}
              className="flex items-center gap-3 px-4 py-2.5 hover:bg-[#F4F3F0] cursor-pointer">
              <input
                type="checkbox"
                checked={selectedIds.includes(u.id)}
                onChange={() => toggle(u.id)}
                className="accent-[#E8956D]"
              />
              <span className="text-sm text-[#3A322C]">{u.name}</span>
              {u.role_name && (
                <span className="text-xs text-[#8A7E73] ml-auto">{u.role_name}</span>
              )}
            </label>
          ))}
        </div>
      )}

      {/* Click-outside close */}
      {open && (
        <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
      )}
    </div>
  );
}

const PRIORITY_COLORS = { low: "text-green-600 bg-green-50", medium: "text-amber-600 bg-amber-50", high: "text-red-600 bg-red-50" };
const STATUS_COLORS = { pending: "text-yellow-700 bg-yellow-50", in_progress: "text-blue-700 bg-blue-50", completed: "text-green-700 bg-green-50", cancelled: "text-gray-600 bg-gray-50" };

export default function TaskManager() {
  const { user } = useAuth();
  const { confirm, ConfirmModal } = useConfirm();
  const [activeTab, setActiveTab] = useState("team");
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);
  const [form, setForm] = useState({});
  const [assigneeIds, setAssigneeIds] = useState([]); // NEW: multi-assignee
  const [comment, setComment] = useState("");
  const [updateText, setUpdateText] = useState("");
  const [detailTab, setDetailTab] = useState("updates"); // "updates" | "comments"
  const [error, setError] = useState("");

  const userId = user?._id || user?.id || "";

  useEffect(() => { fetchTasks(); }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { usersAPI.getAll().then(r => setUsers(r.data || [])).catch(() => {}); }, []);

  const fetchTasks = async () => {
    setLoading(true);
    try {
      const params = activeTab === "private" ? { is_private: true } : { is_private: false };
      const r = await tasksAPI.getAll(params);
      setTasks(r.data || []);
    } catch (e) { if (process.env.NODE_ENV === 'development') console.error('Fetch error:', e); }
    setLoading(false);
  };

  const handleCreate = async () => {
    setError("");
    if (!form.title?.trim()) { setError("Title is required"); return; }
    try {
      await tasksAPI.create({
        ...form,
        assignee_ids: assigneeIds,      // NEW: send multiple assignees
        is_private: activeTab === "private",
      });
      setModal(null); setForm({}); setAssigneeIds([]);
      fetchTasks();
    } catch (e) { setError(formatError(e)); }
  };

  const handleStatusUpdate = async (taskId, status) => {
    try { await tasksAPI.update(taskId, { status }); fetchTasks(); }
    catch (e) { if (process.env.NODE_ENV === 'development') console.error('Fetch error:', e); }
  };

  const handleDelete = async (taskId) => {
    if (!await confirm("Delete task?")) return;
    try { await tasksAPI.delete(taskId); fetchTasks(); } catch (e) { if (process.env.NODE_ENV === 'development') console.error('Fetch error:', e); }
  };

  const handleAddComment = async () => {
    if (!comment.trim() || !selectedTask) return;
    try {
      await tasksAPI.addComment(selectedTask.id, comment);
      setComment("");
      const r = await tasksAPI.getAll(activeTab === "private" ? { is_private: true } : { is_private: false });
      setTasks(r.data || []);
      const updated = r.data.find(t => t.id === selectedTask.id);
      if (updated) setSelectedTask(updated);
    } catch (e) { if (process.env.NODE_ENV === 'development') console.error('Fetch error:', e); }
  };

  const handleAddUpdate = async () => {
    if (!updateText.trim() || !selectedTask) return;
    try {
      await tasksAPI.addUpdate(selectedTask.id, updateText);
      setUpdateText("");
      const r = await tasksAPI.getAll(activeTab === "private" ? { is_private: true } : { is_private: false });
      setTasks(r.data || []);
      const updated = r.data.find(t => t.id === selectedTask.id);
      if (updated) setSelectedTask(updated);
    } catch (e) { if (process.env.NODE_ENV === 'development') console.error('Fetch error:', e); }
  };

  const openTask = (task) => { setSelectedTask(task); setDetailTab("updates"); setModal("view"); };

  // Helper: get display names for a task's assignees
  const getAssigneeDisplay = (task) => {
    if (task.assignees?.length > 0) {
      return task.assignees.map(a => a.name).join(", ");
    }
    return task.assigned_to_name || null;
  };

  const displayTasks = tasks;

  return (
    <div className="space-y-6 max-w-6xl" data-testid="task-manager">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-3xl font-extrabold font-[Manrope] text-[#3A322C]">Task Manager</h1>
        <button
          onClick={() => { setForm({ priority: "medium" }); setAssigneeIds([]); setModal("create"); setError(""); }}
          className="bg-[#E8956D] text-[#3A322C] rounded-lg px-3 py-2 text-sm font-bold flex items-center gap-1"
          data-testid="create-task-btn"
        >
          <Plus size={14} /> New Task
        </button>
      </div>

      <div className="flex border-b border-[#E5E1DB]">
        {[
          { id: "team",    label: "Team Tasks" },
          { id: "private", label: "My Private Tasks", icon: Lock },
        ].map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            className={`px-4 py-2.5 text-sm font-semibold flex items-center gap-1.5 transition-colors -mb-px ${activeTab === t.id ? "border-b-2 border-[#E8956D] text-[#E8956D]" : "text-[#8A7E73] hover:text-[#5C5147]"}`}>
            {t.icon && <t.icon size={14} />}{t.label}
          </button>
        ))}
      </div>

      {loading ? <div className="flex justify-center py-16"><div className="w-8 h-8 border-3 border-[#E8956D] border-t-transparent rounded-full animate-spin" /></div> : (
        <div className="space-y-3">
          {displayTasks.length === 0 ? (
            <div className="text-center py-16 text-[#8A7E73]">
              <CheckCircle size={48} className="mx-auto mb-3 opacity-20" />
              <p>No tasks here</p>
            </div>
          ) : displayTasks.map(task => (
            <div
              key={task.id}
              onClick={() => openTask(task)}
              className="bg-white border border-[#E5E1DB] rounded-xl p-4 hover:shadow-md transition-all cursor-pointer"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full uppercase ${PRIORITY_COLORS[task.priority]}`}>{task.priority}</span>
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full capitalize ${STATUS_COLORS[task.status]}`}>{task.status?.replace("_", " ")}</span>
                    {task.is_private && <Lock size={12} className="text-[#8A7E73]" />}
                  </div>
                  <p className="font-bold text-[#3A322C]">{task.title}</p>
                  {task.description && <p className="text-sm text-[#8A7E73] mt-1 line-clamp-2">{task.description}</p>}
                  <div className="flex items-center gap-4 mt-2 text-xs text-[#8A7E73] flex-wrap">
                    {getAssigneeDisplay(task) && (
                      <span className="flex items-center gap-1">
                        <Users size={11} />
                        <strong className="text-[#5C5147]">{getAssigneeDisplay(task)}</strong>
                      </span>
                    )}
                    {task.due_date && <span className="flex items-center gap-1"><Clock size={11} />{fmtDate(task.due_date)}</span>}
                    {task.comments?.length > 0 && <span className="flex items-center gap-1"><MessageCircle size={11} />{task.comments.length}</span>}
                  </div>
                </div>
                <button
                  onClick={e => { e.stopPropagation(); handleDelete(task.id); }}
                  className="text-[#C4BAAF] hover:text-[#C2624E] flex-shrink-0"
                >
                  <X size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Modal */}
      {modal === "create" && (
        <Modal title={`New ${activeTab === "private" ? "Private" : "Team"} Task`} onClose={() => setModal(null)}>
          <div className="space-y-4">
            <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Title *</label>
              <input className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]"
                value={form.title || ""} onChange={e => setForm({ ...form, title: e.target.value })} /></div>

            <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Description</label>
              <textarea rows={3} className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D] resize-none"
                value={form.description || ""} onChange={e => setForm({ ...form, description: e.target.value })} /></div>

            {activeTab === "team" && (
              <div>
                <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block flex items-center gap-1">
                  <Users size={11} /> Assign To (select multiple)
                </label>
                <AssigneeSelector users={users} selectedIds={assigneeIds} onChange={setAssigneeIds} />
                {assigneeIds.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {assigneeIds.map(id => {
                      const u = users.find(x => x.id === id);
                      return u ? (
                        <span key={id} className="text-xs bg-[#E8956D]/15 text-[#E8956D] px-2 py-0.5 rounded-full font-bold flex items-center gap-1">
                          {u.name}
                          <button onClick={() => setAssigneeIds(assigneeIds.filter(x => x !== id))}><X size={10} /></button>
                        </span>
                      ) : null;
                    })}
                  </div>
                )}
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Priority</label>
                <select className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]"
                  value={form.priority || "medium"} onChange={e => setForm({ ...form, priority: e.target.value })}>
                  {["low", "medium", "high"].map(p => <option key={p} value={p} className="capitalize">{p}</option>)}
                </select></div>
              <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Due Date</label>
                <input type="date" className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]"
                  value={form.due_date || ""} onChange={e => setForm({ ...form, due_date: e.target.value })} /></div>
            </div>

            {error && <p className="text-sm text-[#C2624E] bg-[#FBEAE7] px-3 py-2 rounded-lg">{error}</p>}
            <div className="flex gap-3">
              <button onClick={() => setModal(null)} className="flex-1 border border-[#E5E1DB] rounded-lg py-2.5 text-sm font-bold text-[#5C5147]">Cancel</button>
              <button onClick={handleCreate} className="flex-1 bg-[#E8956D] rounded-lg py-2.5 text-sm font-bold text-[#3A322C]">Create Task</button>
            </div>
          </div>
        </Modal>
      )}

      {/* View/Detail Modal */}
      {modal === "view" && selectedTask && (
        <Modal title={selectedTask.title} onClose={() => { setModal(null); setSelectedTask(null); }}>
          <div className="space-y-4">
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`text-xs font-bold px-2 py-0.5 rounded-full uppercase ${PRIORITY_COLORS[selectedTask.priority]}`}>{selectedTask.priority}</span>
              <span className={`text-xs font-bold px-2 py-0.5 rounded-full capitalize ${STATUS_COLORS[selectedTask.status]}`}>{selectedTask.status?.replace("_", " ")}</span>
            </div>

            {selectedTask.description && <p className="text-sm text-[#5C5147]">{selectedTask.description}</p>}

            <div className="bg-[#F4F3F0] rounded-xl p-3 text-xs space-y-1.5">
              {/* Assignees — show all */}
              {selectedTask.assignees?.length > 0 ? (
                <div>
                  <span className="text-[#8A7E73]">Assigned to: </span>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {selectedTask.assignees.map(a => (
                      <span key={a.id} className="bg-[#E8956D]/15 text-[#E8956D] font-bold px-2 py-0.5 rounded-full">{a.name}</span>
                    ))}
                  </div>
                </div>
              ) : selectedTask.assigned_to_name ? (
                <p><span className="text-[#8A7E73]">Assigned to: </span><strong>{selectedTask.assigned_to_name}</strong></p>
              ) : null}
              {selectedTask.assigned_by_name && <p><span className="text-[#8A7E73]">Assigned by: </span><strong>{selectedTask.assigned_by_name}</strong></p>}
              {selectedTask.due_date && <p><span className="text-[#8A7E73]">Due: </span><strong>{fmtDate(selectedTask.due_date)}</strong></p>}
            </div>

            {/* Status update */}
            <div>
              <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Update Status</label>
              <div className="flex gap-2 flex-wrap">
                {["pending", "in_progress", "completed", "cancelled"].map(s => (
                  <button key={s}
                    onClick={() => { handleStatusUpdate(selectedTask.id, s); setSelectedTask({ ...selectedTask, status: s }); }}
                    className={`text-xs font-bold px-3 py-1.5 rounded-lg border transition-colors ${selectedTask.status === s ? STATUS_COLORS[s] + " border-current" : "border-[#E5E1DB] text-[#5C5147] hover:bg-[#F4F3F0]"}`}>
                    {s.replace("_", " ")}
                  </button>
                ))}
              </div>
            </div>

            {/* Updates & Comments — tabbed */}
            <div>
              <div className="flex border-b border-[#E5E1DB] mb-3">
                {[
                  { id: "updates",  label: `Updates (${selectedTask.updates?.length || 0})` },
                  { id: "comments", label: `Discussion (${selectedTask.comments?.length || 0})` },
                ].map(t => (
                  <button key={t.id} onClick={() => setDetailTab(t.id)}
                    className={`px-3 py-2 text-xs font-bold transition-colors -mb-px ${detailTab === t.id ? "border-b-2 border-[#E8956D] text-[#E8956D]" : "text-[#8A7E73] hover:text-[#5C5147]"}`}>
                    {t.label}
                  </button>
                ))}
              </div>

              {/* Updates tab */}
              {detailTab === "updates" && (
                <div className="space-y-3">
                  <div className="space-y-2 max-h-52 overflow-y-auto">
                    {(selectedTask.updates || []).length === 0 ? (
                      <p className="text-xs text-[#8A7E73] italic py-2">No updates yet. Post the first one below.</p>
                    ) : [...(selectedTask.updates || [])].reverse().map((u, i) => (
                      <div key={`idx-${i}`} className="border-l-2 border-[#E8956D] pl-3 py-1">
                        <div className="flex items-center justify-between mb-0.5">
                          <span className="text-xs font-bold text-[#3A322C]">{u.user_name}</span>
                          <span className="text-[10px] text-[#8A7E73]">
                            {new Date(u.created_at).toLocaleString("en-IN", { day:"2-digit", month:"short", hour:"2-digit", minute:"2-digit" })}
                          </span>
                        </div>
                        <p className="text-sm text-[#5C5147] whitespace-pre-wrap">{u.text}</p>
                      </div>
                    ))}
                  </div>
                  <div className="space-y-2">
                    <textarea
                      rows={3}
                      className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D] resize-none"
                      placeholder="Write a progress update… (e.g. 'Spoke to vendor, delivery confirmed for tomorrow')"
                      value={updateText}
                      onChange={e => setUpdateText(e.target.value)}
                      onKeyDown={e => e.key === "Enter" && e.ctrlKey && handleAddUpdate()}
                    />
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] text-[#8A7E73]">Ctrl+Enter to post</span>
                      <button
                        onClick={handleAddUpdate}
                        disabled={!updateText.trim()}
                        className="bg-[#E8956D] text-[#3A322C] rounded-lg px-4 py-2 text-sm font-bold disabled:opacity-50"
                        data-testid="post-update-btn">
                        Post Update
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Comments/Discussion tab */}
              {detailTab === "comments" && (
                <div className="space-y-3">
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {(selectedTask.comments || []).length === 0 ? (
                      <p className="text-xs text-[#8A7E73] italic py-2">No comments yet.</p>
                    ) : (selectedTask.comments || []).map((c, i) => (
                      <div key={`idx-${i}`} className="bg-[#F4F3F0] rounded-lg px-3 py-2 text-xs">
                        <p className="font-bold text-[#3A322C]">{c.user_name}</p>
                        <p className="text-[#5C5147] mt-0.5">{c.comment}</p>
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <input
                      className="flex-1 border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D]"
                      placeholder="Add a comment…"
                      value={comment}
                      onChange={e => setComment(e.target.value)}
                      onKeyDown={e => e.key === "Enter" && handleAddComment()}
                    />
                    <button onClick={handleAddComment} className="bg-[#E8956D] text-[#3A322C] rounded-lg px-4 py-2 text-sm font-bold">Send</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </Modal>
      )}

      {ConfirmModal}
    </div>
  );
}

